import { inject, Injectable, InjectionToken, makeEnvironmentProviders } from '@angular/core';
import type { EnvironmentProviders } from '@angular/core';
import type { BzIconDef } from './bz-icon-def';

const BZ_ICONS = new InjectionToken<readonly (readonly BzIconDef[])[]>('BZ_ICONS');

/**
 * Registers icons so `<bz-icon name="...">` can find them by name.
 *
 * Only needed for names that are not known at compile time — a status mapped
 * to an icon, a row action chosen at runtime. For a fixed icon prefer
 * `<bz-icon [icon]="bzIconTruck">`, which needs no registration and lets the
 * bundler drop every icon the application does not mention.
 *
 * Safe to call more than once; the registrations merge.
 */
export function provideBzIcons(icons: readonly BzIconDef[]): EnvironmentProviders {
  return makeEnvironmentProviders([{ provide: BZ_ICONS, useValue: icons, multi: true }]);
}

@Injectable({ providedIn: 'root' })
export class BzIconRegistry {
  private readonly icons = new Map<string, BzIconDef>();

  constructor() {
    for (const batch of inject(BZ_ICONS, { optional: true }) ?? []) {
      for (const icon of batch) {
        this.icons.set(icon.name, icon);
      }
    }
  }

  get(name: string): BzIconDef | undefined {
    return this.icons.get(name);
  }
}
